import { SearchIcon, XIcon, InfoIcon, AlertIcon, StopIcon, TriangleDownIcon } from '@primer/octicons-react';
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { AnchoredOverlay } from '../AnchoredOverlay/AnchoredOverlay.js';
import { FilteredActionList } from '../FilteredActionList/FilteredActionListEntry.js';
import Heading from '../Heading/Heading.js';
import { SelectPanelMessage } from './SelectPanelMessage.js';
import { IconButton } from '../Button/IconButton.js';
import '../Button/ButtonBase.js';
import '../utils/defaultSxProp.js';
import { ButtonComponent } from '../Button/Button.js';
import { useProvidedRefOrCreate } from '../hooks/useProvidedRefOrCreate.js';
import '@primer/behaviors/utils';
import '@primer/behaviors';
import { useProvidedStateOrCreate } from '../hooks/useProvidedStateOrCreate.js';
import { useId } from '../hooks/useId.js';
import useSafeTimeout from '../hooks/useSafeTimeout.js';
import { FilteredActionListLoadingTypes } from '../FilteredActionList/FilteredActionListLoaders.js';
import '../FeatureFlags/FeatureFlags.js';
import { useFeatureFlag } from '../FeatureFlags/useFeatureFlag.js';
import '../FeatureFlags/DefaultFeatureFlags.js';
import { announce } from '@primer/live-region-element';
import classes from './SelectPanel.module.css.js';
import { clsx } from 'clsx';
import { heightMap } from '../Overlay/Overlay.js';
import { debounce } from '../node_modules/@github/mini-throttle/dist/index.js';
import { useResponsiveValue } from '../hooks/useResponsiveValue.js';

function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }

// we add a delay so that it does not interrupt default screen reader announcement and queues after it
const SHORT_DELAY_MS = 500;
const LONG_DELAY_MS = 1000;
const DefaultEmptyMessage = /*#__PURE__*/React.createElement(SelectPanelMessage, {
  variant: "empty",
  title: "You haven't created any items yet",
  key: "empty-message"
}, "Please add or create new items to populate the list.");
async function announceText(text, delayMs = SHORT_DELAY_MS) {
  const liveRegion = document.querySelector('live-region');
  liveRegion === null || liveRegion === undefined ? undefined : liveRegion.clear(); // clear previous announcements

  await announce(text, {
    delayMs,
    from: liveRegion ? liveRegion : undefined // announce will create a liveRegion if it doesn't find one
  });
}
async function announceLoading() {
  await announceText('Loading.');
}
const announceNoItems = debounce(message => {
  announceText(message !== null && message !== undefined ? message : 'No matching items.', LONG_DELAY_MS);
}, 250);

// onCancel is optional with variant=anchored, but required with variant=modal

function isMultiSelectVariant(selected) {
  return Array.isArray(selected);
}
const focusZoneSettings = {
  // Let FilteredActionList handle focus zone
  disabled: true
};
const areItemsEqual = (itemA, itemB) => {
  // prefer checking equivality by item.id
  if (typeof itemA.id !== 'undefined') return itemA.id === itemB.id;else return itemA === itemB;
};
const doesItemsIncludeItem = (items, item) => {
  return items.some(i => areItemsEqual(i, item));
};
function SelectPanel({
  open,
  onOpenChange,
  renderAnchor = props => {
    const {
      children,
      ...rest
    } = props;
    return /*#__PURE__*/React.createElement(ButtonComponent, _extends({
      trailingAction: TriangleDownIcon
    }, rest), children);
  },
  anchorRef: externalAnchorRef,
  placeholder,
  placeholderText = 'Filter items',
  inputLabel = placeholderText,
  selected,
  title = isMultiSelectVariant(selected) ? 'Select items' : 'Select an item',
  subtitle,
  onSelectedChange,
  filterValue: externalFilterValue,
  onFilterChange: externalOnFilterChange,
  items,
  footer,
  textInputProps,
  overlayProps,
  sx,
  loading,
  initialLoadingType = 'spinner',
  className,
  height,
  width,
  id,
  message,
  notice,
  onCancel,
  variant = 'anchored',
  ...listProps
}) {
  const titleId = useId();
  const subtitleId = useId();
  const [dataLoadedOnce, setDataLoadedOnce] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [filterValue, setInternalFilterValue] = useProvidedStateOrCreate(externalFilterValue, undefined, '');
  const {
    safeSetTimeout,
    safeClearTimeout
  } = useSafeTimeout();
  const loadingDelayTimeoutId = useRef(null);
  const loadingManagedInternally = loading === undefined;
  const loadingManagedExternally = !loadingManagedInternally;
  const [inputRef, setInputRef] = React.useState(null);
  const [listContainerElement, setListContainerElement] = useState(null);
  const [needsNoItemsAnnouncement, setNeedsNoItemsAnnouncement] = useState(false);
  const isNarrowScreenSize = useResponsiveValue({
    narrow: true,
    regular: false,
    wide: false
  }, false);
  const usingModernActionList = useFeatureFlag('primer_react_select_panel_modern_action_list');
  const usingFullScreenOnNarrow = useFeatureFlag('primer_react_select_panel_fullscreen_on_narrow');

  // Single select modals work differently, they have an intermediate state where the user has selected an item but
  // has not yet confirmed the selection. This is the only time the user can cancel the selection.
  const isSingleSelectModal = variant === 'modal' && !isMultiSelectVariant(selected);
  const [intermediateSelected, setIntermediateSelected] = useState(isSingleSelectModal ? selected : undefined);

  // Reset the intermediate selected item when the panel is open/closed
  useEffect(() => {
    setIntermediateSelected(isSingleSelectModal ? selected : undefined);
  }, [isSingleSelectModal, open, selected]);
  const onListContainerRefChanged = useCallback(node => {
    setListContainerElement(node);
    if (!node && needsNoItemsAnnouncement) {
      announceNoItems();
      setNeedsNoItemsAnnouncement(false);
    }
  }, [needsNoItemsAnnouncement]);
  const onInputRefChanged = useCallback(ref => {
    setInputRef(ref);
  }, [setInputRef]);
  const onFilterChange = useCallback((value, e) => {
    if (loadingManagedInternally) {
      if (loadingDelayTimeoutId.current) {
        safeClearTimeout(loadingDelayTimeoutId.current);
      }
      if (dataLoadedOnce) {
        // If data has already been loaded once, delay the spinner a bit. This also helps
        // not show and then immediately hide the spinner if items are loaded quickly, i.e.
        // not async.

        loadingDelayTimeoutId.current = safeSetTimeout(() => {
          setIsLoading(true);
          announceLoading();
        }, LONG_DELAY_MS);
      } else {
        // If this is the first data load and there are no items, show the loading spinner
        // immediately

        if (items.length === 0) {
          setIsLoading(true);
        }

        // We still want to announce if loading is taking too long
        loadingDelayTimeoutId.current = safeSetTimeout(() => {
          announceLoading();
        }, LONG_DELAY_MS);
      }
    }
    externalOnFilterChange(value, e);
    setInternalFilterValue(value);
  }, [loadingManagedInternally, externalOnFilterChange, setInternalFilterValue, dataLoadedOnce, safeSetTimeout, safeClearTimeout, items.length]);

  // disable body scroll when the panel is open on narrow screens
  useEffect(() => {
    if (open && isNarrowScreenSize && usingFullScreenOnNarrow) {
      const bodyOverflowStyle = document.body.style.overflow || '';
      // If the body is already set to overflow: hidden, it likely means
      // that there is already a modal open. In that case, we should bail
      // so we don't re-enable scroll after the second dialog is closed.
      if (bodyOverflowStyle === 'hidden') {
        return;
      }
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = bodyOverflowStyle;
      };
    }
  }, [isNarrowScreenSize, open, usingFullScreenOnNarrow]);
  useEffect(() => {
    if (open) {
      if (items.length === 0 && !(isLoading || loading)) {
        // we need to wait for the listContainerElement to disappear before announcing no items, otherwise it will be interrupted
        if (!listContainerElement || !usingModernActionList) {
          announceNoItems(message === null || message === undefined ? undefined : message.title);
        } else {
          setNeedsNoItemsAnnouncement(true);
        }
      }
    }
    if (loadingManagedExternally) {
      if (items.length > 0) {
        setDataLoadedOnce(true);
      }
      return;
    }
    if (isLoading || items.length > 0) {
      setIsLoading(false);
      setDataLoadedOnce(true);
    }
    if (loadingDelayTimeoutId.current) {
      safeClearTimeout(loadingDelayTimeoutId.current);
    }

    // Only fire this effect if items have changed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);
  useEffect(() => {
    if (inputRef !== null && inputRef !== undefined && inputRef.current) {
      const ref = inputRef.current;

      // We would normally expect AnchoredOverlay's focus trap to automatically focus the input,
      // but for some reason the ref isn't populated until _after_ the panel is open, which is
      // too late. So, we focus manually here.
      if (open) {
        ref.focus();
      }
    }
  }, [inputRef, open]);

  // Populate panel with items on first open
  useEffect(() => {
    if (loadingManagedExternally) return;

    // If data was already loaded once, do nothing
    if (dataLoadedOnce) return;

    // Only load data when the panel is open
    if (open) {
      // Only trigger filter change event if there are no items
      if (items.length === 0) {
        // Trigger filter event to populate panel on first open
        onFilterChange(filterValue, null);
      }
    }
  }, [open, dataLoadedOnce, onFilterChange, filterValue, items, loadingManagedExternally, listContainerElement]);
  const anchorRef = useProvidedRefOrCreate(externalAnchorRef);
  const onOpen = useCallback(gesture => onOpenChange(true, gesture), [onOpenChange]);
  const onClose = useCallback(gesture => {
    // Clicking outside should cancel the selection only on modals
    if (variant === 'modal' && gesture === 'click-outside') {
      onCancel === null || onCancel === undefined ? undefined : onCancel();
    }
    onOpenChange(false, gesture);
  }, [onOpenChange, variant, onCancel]);
  const onCancelRequested = useCallback(() => {
    onOpenChange(false, 'cancel');
  }, [onOpenChange]);
  const renderMenuAnchor = useMemo(() => {
    if (renderAnchor === null) {
      return null;
    }
    const selectedItems = Array.isArray(selected) ? selected : [...(selected ? [selected] : [])];
    return props => {
      return renderAnchor({
        ...props,
        children: selectedItems.length ? selectedItems.map(item => item.text).join(', ') : placeholder
      });
    };
  }, [placeholder, renderAnchor, selected]);
  const isItemCurrentlySelected = useCallback(item => {
    // For multi-select, we just need to check if the item is in the selected array
    if (isMultiSelectVariant(selected)) {
      return doesItemsIncludeItem(selected, item);
    }

    // For single-select modal, there is an intermediate state when the user has selected
    // an item but has not yet saved the selection. We need to check for this state.
    if (isSingleSelectModal) {
      return (intermediateSelected === null || intermediateSelected === undefined ? undefined : intermediateSelected.id) === item.id;
    }

    // For single-select anchored, we just need to check if the item is the selected item
    return (selected === null || selected === undefined ? undefined : selected.id) === item.id;
  }, [selected, intermediateSelected, isSingleSelectModal]);
  const itemsToRender = useMemo(() => {
    return items.map(item => {
      return {
        ...item,
        role: 'option',
        selected: 'selected' in item && item.selected === undefined ? undefined : isItemCurrentlySelected(item),
        onAction: (itemFromAction, event) => {
          var _item$onAction;
          (_item$onAction = item.onAction) === null || _item$onAction === undefined ? undefined : _item$onAction.call(item, itemFromAction, event);
          if (event.defaultPrevented) {
            return;
          }
          if (isMultiSelectVariant(selected)) {
            const otherSelectedItems = selected.filter(selectedItem => !areItemsEqual(selectedItem, item));
            const newSelectedItems = doesItemsIncludeItem(selected, item) ? otherSelectedItems : [...otherSelectedItems, item];
            const multiSelectOnChange = onSelectedChange;
            multiSelectOnChange(newSelectedItems);
            return;
          }
          if (isSingleSelectModal) {
            if ((intermediateSelected === null || intermediateSelected === undefined ? undefined : intermediateSelected.id) === item.id) {
              // if the item is already selected, we need to unselect it
              setIntermediateSelected(undefined);
            } else {
              setIntermediateSelected(item);
            }
            return;
          }
          // single select anchored, direct save on click
          const singleSelectOnChange = onSelectedChange;
          singleSelectOnChange(item === selected ? undefined : item);
          onClose('selection');
        }
      };
    });
  }, [onClose, onSelectedChange, items, selected, isItemCurrentlySelected, isSingleSelectModal, intermediateSelected]);
  const focusTrapSettings = {
    initialFocusRef: inputRef || undefined
  };
  const extendedTextInputProps = useMemo(() => {
    return {
      sx: {
        m: 2
      },
      contrast: true,
      leadingVisual: SearchIcon,
      'aria-label': inputLabel,
      ...textInputProps
    };
  }, [inputLabel, textInputProps]);
  const loadingType = () => {
    if (dataLoadedOnce) {
      return FilteredActionListLoadingTypes.input;
    } else {
      if (initialLoadingType === 'spinner') {
        return FilteredActionListLoadingTypes.bodySpinner;
      } else {
        return FilteredActionListLoadingTypes.bodySkeleton;
      }
    }
  };
  const iconForNoticeVariant = {
    info: /*#__PURE__*/React.createElement(InfoIcon, {
      size: 16
    }),
    warning: /*#__PURE__*/React.createElement(AlertIcon, {
      size: 16
    }),
    error: /*#__PURE__*/React.createElement(StopIcon, {
      size: 16
    })
  };
  function getMessage() {
    if (items.length === 0 && !message) {
      return DefaultEmptyMessage;
    } else if (message) {
      return /*#__PURE__*/React.createElement(SelectPanelMessage, {
        title: message.title,
        variant: message.variant
      }, message.body);
    }
  }

  // We add a save and cancel button on narrow screens when SelectPanel is full-screen
  const showCancelSaveButtons = variant === 'modal' || isMultiSelectVariant(selected) && usingFullScreenOnNarrow;
  // because of instant selection, canceling on single select is the same as closing the panel, no onCancel needed
  const showXCloseIcon = variant === 'modal' || (onCancel || !isMultiSelectVariant(selected)) && usingFullScreenOnNarrow;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(AnchoredOverlay, {
    renderAnchor: renderMenuAnchor,
    anchorRef: anchorRef,
    open: open,
    onOpen: onOpen,
    onClose: onClose,
    overlayProps: {
      role: 'dialog',
      'aria-labelledby': titleId,
      'aria-describedby': subtitle ? subtitleId : undefined,
      ...overlayProps,
      ...(variant === 'modal' ? {
        /* override AnchoredOverlay position */
        top: '50vh',
        left: '50vw',
        anchorSide: undefined
      } : {}),
      style: {
        '--max-height': overlayProps !== null && overlayProps !== undefined && overlayProps.maxHeight ? heightMap[overlayProps.maxHeight] : heightMap['large'],
        /* override AnchoredOverlay position */
        transform: variant === 'modal' ? 'translate(-50%, -50%)' : undefined
      }
    },
    focusTrapSettings: focusTrapSettings,
    focusZoneSettings: focusZoneSettings,
    height: height,
    width: width,
    anchorId: id,
    variant: usingFullScreenOnNarrow ? {
      regular: 'anchored',
      narrow: 'fullscreen'
    } : undefined,
    pinPosition: !height,
    className: classes.Overlay
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.Wrapper,
    "data-variant": variant
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.Header
  }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Heading, {
    as: "h1",
    id: titleId,
    className: classes.Title
  }, title), subtitle ? /*#__PURE__*/React.createElement("div", {
    id: subtitleId,
    className: classes.Subtitle
  }, subtitle) : null), showXCloseIcon ? /*#__PURE__*/React.createElement(IconButton, {
    type: "button",
    variant: "invisible",
    icon: XIcon,
    "aria-label": "Cancel and close",
    className: classes.ResponsiveCloseButton,
    onClick: () => {
      onCancel === null || onCancel === undefined ? undefined : onCancel();
      onCancelRequested();
    }
  }) : null), notice && /*#__PURE__*/React.createElement("div", {
    "aria-live": "polite",
    "data-variant": notice.variant,
    className: classes.Notice
  }, iconForNoticeVariant[notice.variant], /*#__PURE__*/React.createElement("div", null, notice.text)), /*#__PURE__*/React.createElement(FilteredActionList, _extends({
    filterValue: filterValue,
    onFilterChange: onFilterChange,
    onListContainerRefChanged: onListContainerRefChanged,
    onInputRefChanged: onInputRefChanged,
    placeholderText: placeholderText
  }, listProps, {
    role: "listbox"
    // browsers give aria-labelledby precedence over aria-label so we need to make sure
    // we don't accidentally override props.aria-label
    ,
    "aria-labelledby": listProps['aria-label'] ? undefined : titleId,
    "aria-multiselectable": isMultiSelectVariant(selected) ? 'true' : 'false',
    selectionVariant: isSingleSelectModal ? 'radio' : isMultiSelectVariant(selected) ? 'multiple' : 'single',
    items: itemsToRender,
    textInputProps: extendedTextInputProps,
    loading: loading || isLoading,
    loadingType: loadingType()
    // hack because the deprecated ActionList does not support this prop
    ,

    message: getMessage(),
    // inheriting height and maxHeight ensures that the FilteredActionList is never taller
    // than the Overlay (which would break scrolling the items)
    sx: sx,
    className: clsx(className, classes.FilteredActionList)
    // needed to explicitly enable announcements for deprecated FilteredActionList, we can remove when we fully remove the deprecated version
    ,
    announcementsEnabled: true
  })), footer ? /*#__PURE__*/React.createElement("div", {
    className: classes.Footer
  }, footer) : showCancelSaveButtons ?
  /*#__PURE__*/
  /* Save and Cancel buttons are only useful for multiple selection, single selection instantly closes the panel */
  React.createElement("div", {
    className: clsx(classes.Footer, classes.ResponsiveFooter)
  }, onCancel && /*#__PURE__*/React.createElement(ButtonComponent, {
    size: "medium",
    onClick: () => {
      onCancel();
      onCancelRequested();
    }
  }, "Cancel"), /*#__PURE__*/React.createElement(ButtonComponent, {
    block: onCancel === undefined,
    variant: "primary",
    size: "medium",
    onClick: () => {
      if (isSingleSelectModal) {
        const singleSelectOnChange = onSelectedChange;
        singleSelectOnChange(intermediateSelected);
      }
      onClose(variant === 'modal' ? 'selection' : 'click-outside');
    }
  }, "Save")) : null)), variant === 'modal' && open ? /*#__PURE__*/React.createElement("div", {
    className: classes.Backdrop
  }) : null);
}

export { SelectPanel };
